import { Block, Blocks } from 'factories/block';
import { setExitCompleteAction } from 'actions/setExitCompleteAction';
import { updateScoreAction } from 'actions/updateScoreAction';
import { ModuleContext } from 'components/Module/Module_v2';
import { SCORMProps, ScoringCriteria, createScorm } from 'factories/scorm';
import { useHostingSession } from 'hosting/context';
import pipwerks, { SCORM } from 'pipwerks-scorm-api-wrapper';
import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import getAssessments from 'services/apiHelpers/getAssessments';
import getBlockById from 'services/apiHelpers/getBlockById';
import getCurrentTopic from 'services/apiHelpers/getCurrentTopic';
import getInteractionsInTopic from 'services/apiHelpers/getInteractionsInTopic';
import getCurrentLocation from 'services/getCurrentLocation';
import { stripNonASCIICharactersFromString } from 'services/stripNonASCIICharactersFromString';
import getStore from 'services/getStore';
import pubSub from 'services/pubSub';
import {
  transformAssessmentsFromSuspendData,
  transformAssessmentsToSuspendData,
} from 'services/transformAssessmentsToAndFromSuspendData';
import {
  transformListFromIdToSId,
  transformListFromSIdToId,
} from 'services/transformListBetweenIdSId';
import {
  transformScoreDataFromIdToSId,
  transformScoreDataFromSIdToId,
} from 'services/transformScoreData';
import {
  Assessments,
  LandingPagesSeenState,
  LessonStatus,
  Report,
  State,
} from 'store/store';
import {
  findBlocksIn,
  getAllInteractionsOrdered,
} from '../../../selectors/block';
import API from '../../../services/scorm/API';
import SCORMToHosting from './SCORMToHosting';

const API_KEYS = API;

enum Version {
  OnePointTwo = '1.2',
  TwoThousandAndFour = '2004',
}

enum SuccessString {
  Passed = 'passed',
  Failed = 'failed',
}

enum CompletionString {
  Complete = 'completed',
  Incomplete = 'incomplete',
}

enum ShouldSendScore {
  Always = 'ALWAYS',
  Never = 'NEVER',
  OnComplete = 'COMPLETE',
}

export enum ReportableInteractions {
  Multichoice = 'multichoice',
  Sortation = 'sortation',
  Likert = 'likert',
  FillIn = 'fillin',
  LongFillIn = 'longfillin',
  // quiz ?
}

let scormInitialized;
//
export const getSCORMInitialized = () => {
  return scormInitialized;
};

function getSCORMIsInReviewMode(SCORM) {
  return [
    SCORM.get('cmi.mode')?.toLowerCase(),
    SCORM.get('cmi.core.lesson_mode')?.toLowerCase(),
  ]?.includes('review');
}

const getResult = (version, result) => {
  if (version === Version.OnePointTwo) {
    return result ? 'correct' : 'wrong';
  }

  return result ? 'correct' : 'incorrect';
};

const asPercent = (number: number, total: number) => {
  return ((number || 0) / total) * 100 || 0;
};

// TO not DO.
// for sam. discusss this bit
// boolean to ensure we only run the scoreData reloading once
let hasReloadedScoreData: boolean = false;

// initialize location here and / so as it's updated
// is available to module
// sending to suspend_data for example. which comes after a timeout
let location = '';

/**
 * ExitProps
 *
 * When the module is preparing to exit, it needs to make sure that all the appropriate
 * controls are triggered in the SCORM container. The data in ExitProps is a complete
 * representation of this data and as such can be used to toggle saving behaviours further
 * down the chain
 */
interface ExitProps {
  lastInteractionSeen: boolean;
  allInteractionsSeen: boolean;
  passed: boolean;
  isComplete: boolean;
  moduleWasManuallySetComplete: undefined | boolean;
  hasExited: boolean;
  scoreRaw: number;
  scoreMax: number;
  scorePercent: number;
  percentComplete: number;
  sendScore: boolean;
  moduleCompleteInterationRequirement: string;
  moduleCompleteScoreRequirement: boolean;
  shouldSendScoreWhen: string;
  sendOnlySuccessStatus: boolean;
  lessonStatus: LessonStatus;
  scoringCriteria: ScoringCriteria;
  passMark?: number | null;
}

export const useExitProps = (): ExitProps => {
  const state = getStore().getState();

  const module = Object.values(state.blocks).find(b => {
    return (b as Block)?.type === 'Module';
  }) as Block;

  let scormBlock = Object.values(state.blocks).find(b => {
    return (b as Block)?.type === 'SCORM';
  }) as Block;

  if (!scormBlock) {
    scormBlock = createScorm();
  }

  if (!module) {
    throw Error('Unable to find module block');
  }

  // I think this comes from
  const {
    shouldSendScoreWhen,
    moduleCompleteInterationRequirement,
    moduleCompleteScoreRequirement,
    sendOnlySuccessStatus = false,
    lessonStatus = 'completed/incomplete',
    scoringCriteria,
  } = scormBlock.props as SCORMProps;

  const passMark = module?.props?.passMark;

  const { interactionsSeen, scoreData = {}, exiting } = useSelector(
    (state: State) => {
      return {
        interactionsSeen: state.interactionsSeen,
        scoreData: state.userBehaviour.score,
        exiting: state.exit,
      };
    }
  );

  const allInteractions = useSelector(getAllInteractionsOrdered); //
  const scoringInteractions = useSelector(loadScoringBlocks);
  const numberOfScorableInteractions = scoringInteractions.length;

  const [lastInteractionSeen, setLastInteractionSeen] = useState(false);

  const moduleWasManuallySetComplete = useSelector((state: State) => {
    return state.moduleWasManuallySetComplete;
  });

  useEffect(() => {
    if (moduleWasManuallySetComplete === undefined) {
      return;
    }

    setSCORMCompletion(
      SCORM,
      (SCORM.version as Version) || Version.TwoThousandAndFour
    )(moduleWasManuallySetComplete);
  }, [moduleWasManuallySetComplete]);

  useEffect(() => {
    if (!allInteractions.length) {
      return;
    }
    if (!interactionsSeen.length) {
      return;
    }

    const lastInteraction = allInteractions[allInteractions.length - 1].id;

    if (interactionsSeen.includes(lastInteraction)) {
      setLastInteractionSeen(() => true);
    }

    //
  }, [interactionsSeen, allInteractions, setLastInteractionSeen]);

  const allInteractionsSeen =
    interactionsSeen &&
    allInteractions &&
    interactionsSeen.length === allInteractions.length;

  let scoreRaw = Object.values(scoreData as { [key: string]: number }).reduce<
    number
  >((acc, curr) => {
    // initialize _curr_ default
    let _curr_ = curr;

    // in the case where we might have exisitng minimized vales of 't' || 'f'
    //
    switch (_curr_.toString()) {
      case 't': {
        _curr_ = 1;
        break;
      }
      case 'f': {
        _curr_ = 0;
        break;
      }
      // and tap it again for good measure
      default:
        _curr_ = curr;
    }
    //

    // in the case where there could still exist a boolean
    // calculates fine implicitly but lets explicitly numberify it
    return acc + Number(_curr_);
  }, 0);

  let scorePercent = ((scoreRaw || 0) / numberOfScorableInteractions) * 100;

  // we'll default the score to 100 if. . . ?
  if (!moduleCompleteScoreRequirement) {
    // scorePercent = 100;
  }

  if (numberOfScorableInteractions <= 0) {
    scorePercent = 100;
  }

  const percentComplete =
    (interactionsSeen.length / allInteractions.length) * 100;

  const exitProps: ExitProps = {
    lastInteractionSeen,
    allInteractionsSeen,
    scoreRaw,
    scoreMax: numberOfScorableInteractions,
    passed: scorePercent >= Number(passMark),
    scorePercent,
    percentComplete,
    isComplete: false,
    moduleWasManuallySetComplete,
    sendScore: false,
    hasExited: exiting,
    shouldSendScoreWhen,
    moduleCompleteInterationRequirement,
    moduleCompleteScoreRequirement,
    sendOnlySuccessStatus,
    lessonStatus,
    scoringCriteria,
    passMark,
  };

  const interactionCompletion =
    exitProps[exitProps.moduleCompleteInterationRequirement];

  const scoreCompletion = exitProps.moduleCompleteScoreRequirement
    ? exitProps.passed
    : true;

  const isComplete =
    moduleWasManuallySetComplete || (interactionCompletion && scoreCompletion);

  exitProps.isComplete = isComplete;

  return exitProps;
};

/**
 * scormFunc
 *
 * A scorm function is a chainable side effect function. It takes some data, possibly performs
 * and action that changes remote data (eg. scorm container function call) updating the props in
 * the process and then returns a new ExitProps object.
 *
 * By chaining these functions we can create easy to follow processes for saving data on exit
 */
type scormFunc = (e: ExitProps) => ExitProps;

type SuspendData = {
  interactionsSeen: string[];
  interactionsLocked?: string[];
  landingPagesSeenState?: LandingPagesSeenState;
  // 'loosely' typed types here
  interactionsReporting: { [key: string]: any };
  scoreData: { [key: string]: any };
  assessments: Assessments;
  moduleVariables: { [key: string]: string };
  location: string;
};

//
//
let module;
export const assessmentScoringAverageOrFirstPassed = (data: {
  scorm: SCORM | null;
  version: string;
  scoringCriteria: ScoringCriteria;
  lessonStatus: LessonStatus;
  lastInteractionSeen: boolean;
  allInteractionsSeen: boolean;
  moduleCompleteInterationRequirement: string;
  moduleCompleteScoreRequirement: boolean;
}) => {
  const {
    scorm,
    version,
    scoringCriteria,
    lessonStatus,
    lastInteractionSeen,
    allInteractionsSeen,
    moduleCompleteInterationRequirement,
    moduleCompleteScoreRequirement,
  } = data;

  // get fresh state
  const state = getStore().getState();

  // get the passMark on module props
  // can we get this from somewhere else ? prior
  if (!module) {
    module = Object.values(state.blocks).find(b => {
      return (b as Block)?.type === 'Module';
    }) as Block;
  }

  const passMark = module?.props?.passMark;

  // get all the assessments from state

  type TAssessments = {
    [key: string]: {
      data: {
        scoring: boolean;
        passed: boolean;
        score: number;
      };
      results: { [key: string]: { id: string; isCorrect: boolean } };
    };
  };

  let assessments = state.assessments;

  if (assessments && Array.isArray(assessments)) {
    assessments = assessments.filter(assessment => {
      // filter out any that might be undefined
      return Object.keys(assessment);
    });
  }

  // break them down as follows. . .
  //

  const passingAssessments = Object.values(assessments as TAssessments).filter(
    assessment => {
      const { scoring, passed } = assessment?.data || {};
      return Boolean(scoring && passed);
    }
  );

  // we use the first assessment. passingAssessments[0]
  // might be that at some time later we want to calculate for more than the first
  const passingAssessment = passingAssessments[0];

  const passingAssessmentResults =
    (passingAssessments?.length &&
      Object.values(passingAssessment?.results).map(r => {
        return r;
      })) ||
    [];

  let scoreMax = 0;
  let scoreRaw = 0;
  let passed = false;

  if (scoringCriteria === 'AVERAGE') {
    scoreMax = (getAssessments() || []).filter((a: Block) => {
      return a?.props?.scoring;
    })?.length;

    //

    type TAssessmentScoringData = {
      [key: string]: {
        scoreFloat: number;
      };
    };

    const assessmentScoringData = {};

    Object.keys(assessments).forEach(assessment => {
      const results: { id: string; isCorrect: boolean }[] = Object.values(
        assessments[assessment]?.results || {}
      );

      if (!assessmentScoringData[assessment]) {
        assessmentScoringData[assessment] = {};
      }

      let assessmentScoreMax = results?.length;
      let assessmentScoreRaw = 0;
      results.forEach((r, index) => {
        //

        const { id, isCorrect } = r;
        assessmentScoreRaw += isCorrect ? 1 : 0;
      });

      const scoreFloat = assessmentScoreRaw / assessmentScoreMax;

      assessmentScoringData[assessment].scoreFloat = scoreFloat;
    });

    scoreRaw = Object.values(
      assessmentScoringData as TAssessmentScoringData
    ).reduce((acc, curr) => {
      return acc + curr.scoreFloat;
    }, 0) as number;

    //
    const percent = Math.round(asPercent(scoreRaw, scoreMax) || 0);
    passed = percent >= passMark;

    console.log({
      // scoreRaw,
      // scoreMax,
      percent: `${percent}%`,
      // passed,
    });
  }

  if (scoringCriteria === 'PASSED_ASSESSMENTS') {
    scoreMax = passingAssessmentResults.length;

    //
    if (passingAssessment && passingAssessment.data) {
      scoreRaw = passingAssessment.data.score;
      passed = passingAssessment.data.passed;
      //
    }
  }

  const isComplete = passed;

  let shouldSendScore = false;

  if (
    moduleCompleteInterationRequirement === 'lastInteractionSeen' &&
    lastInteractionSeen
  ) {
    shouldSendScore = true;
  }

  if (
    moduleCompleteInterationRequirement === 'allInteractionsSeen' &&
    allInteractionsSeen
  ) {
    shouldSendScore = true;
  }

  if (version === '1.2' && lessonStatus === 'passed/failed/incomplete') {
    const completionString = isComplete
      ? 'passed'
      : moduleCompleteScoreRequirement
      ? 'failed'
      : 'incomplete';

    scorm && scorm.set(API[version].completion, completionString);

    console.log('EXPANDED ASSESSMENT(S) FINAL VALUES!!! :', {
      scoreRaw,
      scoreMax,
      passed,
    });

    return {
      scoreMax,
      scoreRaw,
      passed,
      sendScore: shouldSendScore,
    };
  }

  scorm &&
    scorm.set(
      API[version].completion,
      isComplete ? CompletionString.Complete : CompletionString.Incomplete
    );

  console.log('EXPANDED ASSESSMENT(S) FINAL VALUES!!! :', {
    scoreRaw,
    scoreMax,
    passed,
  });

  return {
    scoreMax,
    scoreRaw,
    passed,
    sendScore: shouldSendScore,
  };
};

/**
 * setScoreData
 *
 * saves the score to scorm container IF required
 */
const setScoreData = (scorm: SCORM, version: string): scormFunc => props => {
  const { scoringCriteria } = props;

  let { scoreRaw, scoreMax, passed, sendScore } = props;

  if (
    (['AVERAGE', 'PASSED_ASSESSMENTS'] as ScoringCriteria[]).includes(
      scoringCriteria
    )
  ) {
    const assessmentScoringData = assessmentScoringAverageOrFirstPassed({
      scorm,
      version,
      ...props,
    });

    scoreRaw = assessmentScoringData.scoreRaw;
    scoreMax = assessmentScoringData.scoreMax;
    passed = assessmentScoringData.passed;
    sendScore = assessmentScoringData.sendScore;
  }

  const shouldSendSuccess = props[props.moduleCompleteInterationRequirement];
  //

  if (!sendScore) {
    return props;
  }

  scorm.set(API[version].scoreMin, 0);
  scorm.set(API[version].scoreMax, 100);

  scorm.set(API[version].scoreRaw, asPercent(scoreRaw, scoreMax));

  if (version === Version.TwoThousandAndFour) {
    scorm.set(API[version].scoreScaled, scoreRaw / scoreMax);

    if (shouldSendSuccess) {
      scorm.set(
        API[version].success,
        passed ? SuccessString.Passed : SuccessString.Failed
      );
    }
  }

  return props;
};

/**
 * setSCORMCompletion
 *
 * If the module has been completed, declare it so.
 */
const setSCORMCompletion = (scorm: SCORM, version: string) => (
  isComplete: boolean,
  props = {} as ExitProps
): void => {
  // bail !!!
  const scormCompletionString = scorm.get(API[version].completion);

  if (['completed', 'passed'].includes(scormCompletionString)) {
    return;
  }

  const { passed, moduleCompleteScoreRequirement, lessonStatus } = props;

  if (version === '1.2' && lessonStatus === 'passed/failed/incomplete') {
    const completionString = isComplete
      ? 'passed'
      : moduleCompleteScoreRequirement
      ? 'failed'
      : 'incomplete';

    console.log({ completionString });

    scorm.set(API[version].completion, completionString);

    return;
  }

  scorm.set(
    API[version].completion,
    isComplete ? CompletionString.Complete : CompletionString.Incomplete
  );
};

/**
 * setCompletionStatus
 *
 * chainable version of setSCORMCompletion
 */
const setCompletionStatus = (
  scorm: SCORM,
  version: string
): scormFunc => props => {
  const { isComplete } = props;

  if (version === '2004' && props.sendOnlySuccessStatus) {
    console.log(
      { sendOnlySuccessStatus: props.sendOnlySuccessStatus },
      'we bail early and do not send completion'
    );
    return props;
  }

  if (props[props.moduleCompleteInterationRequirement]) {
    setSCORMCompletion(scorm, version)(isComplete, props);
  }

  return props;
};

/**
 * saveAndQuit
 *
 * shut down the scorm container (should be called last)
 */
const save = (scorm: SCORM): scormFunc => props => {
  scorm.save();

  return props;
};

/**
 * setIsComplete
 *
 * determins whether or not the module is completed based on props
 */
const setIsComplete: scormFunc = props => {
  const interactionCompletion =
    props[props.moduleCompleteInterationRequirement];
  const scoreCompletion = props.moduleCompleteScoreRequirement
    ? props.passed
    : true;

  const isComplete = interactionCompletion && scoreCompletion;

  return { ...props, isComplete };
};

/**
 * setShouldSendScore
 *
 * using data provided, decided if we should be sending the score to the scorm container
 * @param props
 */
const setShouldSendScore: scormFunc = props => {
  const {
    lastInteractionSeen,
    allInteractionsSeen,
    moduleCompleteInterationRequirement,
    moduleCompleteScoreRequirement,
  } = props;

  let shouldSendScore = false;

  switch (props.shouldSendScoreWhen) {
    case ShouldSendScore.Never:
      shouldSendScore = false;
      break;
    case ShouldSendScore.Always:
      shouldSendScore = true;
      break;
    case ShouldSendScore.OnComplete:
      //

      if (
        moduleCompleteInterationRequirement === 'lastInteractionSeen' &&
        lastInteractionSeen
      ) {
        shouldSendScore = true;
      }

      if (
        moduleCompleteInterationRequirement === 'allInteractionsSeen' &&
        allInteractionsSeen
      ) {
        shouldSendScore = true;
      }

      break;
  }

  return {
    ...props,
    sendScore: shouldSendScore ? true : false,
  };
};

/**
 * setCompleteOnExitOps
 *
 * the chain of operations for saving
 */
const setCompleteOnExitOps = (
  scorm: SCORM,
  version: string,
  exitProps: ExitProps
) => {
  const funcs = [
    setIsComplete,
    setShouldSendScore,
    setScoreData(scorm, version),
    setCompletionStatus(scorm, version),
    save(scorm),
  ];

  return funcs.reduce((cur: ExitProps, next: scormFunc) => {
    const props = next(cur);

    return props;
  }, exitProps);
};

/**
 * q contains all the data that an interaction sends when it wants to report to
 * the scorm container
 */

type q = Report & { success: string };

type qFunc = (p: q) => void;

// returning early on all of these for testing
const qid: qFunc = ({ id, key }) => {
  SCORM.set(`cmi.interactions.${key}.id`, id);
};

const qdesc: qFunc = ({ key, question = '' }) => {
  SCORM.set(`cmi.interactions.${key}.description`, question?.substring(0, 250));
};

const qtype: qFunc = ({ key, type }) => {
  SCORM.set(`cmi.interactions.${key}.type`, type);
};

const qstudent: qFunc = ({ key, response }) => {
  SCORM.set(`cmi.interactions.${key}.student_response`, response);
};

const qlearner: qFunc = ({ key, response }) => {
  SCORM.set(`cmi.interactions.${key}.learner_response`, response);
};

const qcorrect: qFunc = ({ key, correct }) => {
  SCORM.set(`cmi.interactions.${key}.correct_responses.0.pattern`, correct);
};

const qresult: qFunc = ({ key, result }) => {
  SCORM.set(`cmi.interactions.${key}.result`, result);
};

const qsave: qFunc = () => {
  SCORM.save();
};

type InteractionsReportingSequence = {
  [key in Version]: {
    [key in ReportableInteractions]: qFunc[];
  };
};

/**
 * cmiInteractions is a nested map that contains the sequence functions needed
 * to send interaction reporting data to the SCORM container.
 */
const cmiInteractions: InteractionsReportingSequence = {
  [Version.OnePointTwo]: {
    [ReportableInteractions.Multichoice]: [
      qid,
      qtype,
      qcorrect,
      qstudent,
      qresult,
      qsave,
    ],
    [ReportableInteractions.Sortation]: [
      qid,
      qtype,
      qcorrect,
      qstudent,
      qresult,
      qsave,
    ],
    [ReportableInteractions.Likert]: [
      qid,
      qtype,
      qcorrect,
      qstudent,
      qresult,
      qsave,
    ],
    [ReportableInteractions.FillIn]: [
      qid,
      qtype,
      qstudent,
      qcorrect,
      qresult,
      qsave,
    ],
    [ReportableInteractions.LongFillIn]: [
      qid,
      qtype,
      qstudent,
      qcorrect,
      qresult,
      qsave,
    ],
  },
  [Version.TwoThousandAndFour]: {
    [ReportableInteractions.Multichoice]: [
      qid,
      qdesc,
      qtype,
      qlearner,
      qcorrect,
      qresult,
      qsave,
    ],
    [ReportableInteractions.Sortation]: [
      qid,
      qdesc,
      qtype,
      qlearner,
      qcorrect,
      qresult,
      qsave,
    ],
    [ReportableInteractions.Likert]: [
      qid,
      qdesc,
      qtype,
      qlearner,
      qcorrect,
      qresult,
      qsave,
    ],
    [ReportableInteractions.FillIn]: [
      qid,
      qdesc,
      qtype,
      qlearner,
      qcorrect,
      qresult,
      qsave,
    ],
    [ReportableInteractions.LongFillIn]: [
      qid,
      qdesc,
      qtype,
      qlearner,
      qcorrect,
      qresult,
      qsave,
    ],
  },
};

/**
 * setCMIInteractions is an internal function for saving an interaction into the scorm container
 *
 * @param interactionType the type of interaction ( Multichoice | Sortation )
 * @param version version of scorm that we are targeting (1.2 | 2004)
 */
const setCMIInteractions = (
  interactionType: ReportableInteractions,
  version: Version
) => ({
  id,
  question,
  type,
  response,
  correct,
  result,
  reportable,
}: Report) => {
  let count = SCORM.get('cmi.interactions._count');
  const resultParsed = getResult(version, result);

  const qProps: q = {
    key: count,
    id,
    question: question
      ? stripNonASCIICharactersFromString(question)
      : 'pending',
    type,
    reportable,
    response: response
      ? stripNonASCIICharactersFromString(response)
      : 'pending',
    correct: correct ? stripNonASCIICharactersFromString(correct) : 'pending',
    result: resultParsed as any, // this is BAD
    success: API_KEYS[version].result[result],
  };

  console.log(cmiInteractions, qProps, {
    version,
    interactionType,
  });

  cmiInteractions[version][interactionType]?.reduce((_, next) => {
    next(qProps);
    return true;
  }, false);
};

/**
 * activeBlockSelector finds blocks that are accessible from the root block
 */
const activeBlockSelector = (blocks: Blocks, rootId: string): Block[] => {
  return findBlocksIn(blocks, blocks[rootId], (i: any) => i);
};

/**
 * scoringBlocks finds all the blocks that are scorable and have it turned on
 */
const scoringBlocks = (blocks: Block[]): string[] => {
  const moduleBlock = (Object.values(
    getStore().getState().blocks
  ) as Block[]).find(b => b.type === 'Module');

  const includeAllScoringInteractions = false;

  const assessments = getAssessments();

  const scoringAssessments = assessments.filter((a: Block) => {
    return a?.props?.scoring;
  });

  let assessmentsQuestions: string[] = [];

  if (scoringAssessments.length) {
    scoringAssessments.forEach((a: any) => {
      const interactionsInTopic = getInteractionsInTopic(a);

      assessmentsQuestions = [
        ...assessmentsQuestions,
        ...interactionsInTopic
          .filter((q, i) => {
            return i > 0 && i < interactionsInTopic.length - 1;
          })
          .map(q => {
            return q.id;
          }),
      ];
      //
    });
  }

  if (assessmentsQuestions.length) {
    return assessmentsQuestions;
  }

  const sB = [
    ...blocks
      .filter((b: any) => {
        return b?.props?.score && b.props.score === true;
      })
      .map((b: any) => b.id),
  ];

  return sB;
};

/**
 * This selector pulls out all the active blocks from the store
 */
const loadActiveBlocks = createSelector(
  [(state: State) => state.blocks, (state: State) => state.rootBlockId!],
  activeBlockSelector
);

export const loadScoringBlocks = createSelector(
  loadActiveBlocks,
  scoringBlocks
);

/**
 * call exit on scorm based on the options provided to the block
 */
const exit = (initialised, scorm, version, exitProps) => {
  if (!initialised) {
    return;
  }

  setCompleteOnExitOps(scorm, version, exitProps);

  scorm.save();
  scorm.quit();
};

/**
 * This block is responsible for ALL scorm interaction. The core parts are
 *
 * - saving and loading current users progress
 * - saving score and behaviour into the CMI.interactions
 * - setting the score
 * - provide an exit facility ?? (i'm not convinced of this one)
 *
 * @param props
 */

const PIPWERKS = pipwerks as any;
//
export const SCORMRenderer = (props: Block) => {
  // kill all the debug noise in dev
  if (window.location.toString().match('localhost:') && PIPWERKS?.debug) {
    PIPWERKS.debug.isActive = false;
  }

  const scormSettings = props.props;

  const {
    shouldSendScoreWhen,
    moduleCompleteInterationRequirement,
    moduleCompleteScoreRequirement,
    scormExitMessage = 'logout',
    scormControlledExit = false,
    scormExitDelay,
    sendOnlySuccessStatus = false,
    lessonStatus = 'completed/incomplete',
    scoringCriteria,
  } = scormSettings as SCORMProps;

  const dispatch = useDispatch();
  const hostingCtx = useHostingSession();

  let { passMark } = useContext(ModuleContext);

  const [initialised, setInitialized] = useState(false);
  const [version, setVersion] = useState<Version>(Version.OnePointTwo);

  const [lastInteractionSeen, setLastInteractionSeen] = useState(false);

  const [reportedInteractions, setReportedInteractions] = useState({});

  const {
    interactionsSeen,
    interactionsLocked,
    landingPagesSeenState,
    interactionsReporting,
    scoreData = {},
    feedback = {},
    exiting,
    exitComplete,
    moduleWasManuallySetComplete,
    assessments,
    moduleVariables,
    //
    enableBookmarking,
  } = useSelector((state: State) => {
    const { enableBookmarking } = state.theme;

    return {
      interactionsSeen: state.interactionsSeen,
      interactionsLocked: state.interactionsLocked,
      landingPagesSeenState: state.landingPagesSeenState,
      interactionsReporting: state.userBehaviour.scormResponses,
      scoreData: state.userBehaviour.score,
      feedback: state.feedback,
      exiting: state.exit,
      exitComplete: state.exitComplete,
      moduleWasManuallySetComplete: state.moduleWasManuallySetComplete,
      assessments: state.assessments,
      moduleVariables: state.moduleVariables,
      //
      enableBookmarking:
        enableBookmarking === undefined ? true : enableBookmarking,
    };
  });

  const allInteractions = useSelector(getAllInteractionsOrdered); //

  const scoringInteractions = useSelector(loadScoringBlocks);
  const numberOfScorableInteractions = scoringInteractions.length;

  useEffect(() => {
    if (!allInteractions.length) {
      return;
    }

    if (!interactionsSeen.length) {
      return;
    }

    const lastInteraction = allInteractions[allInteractions.length - 1].id;

    if (interactionsSeen.includes(lastInteraction)) {
      setLastInteractionSeen(() => true);
    }
  }, [interactionsSeen, allInteractions, setLastInteractionSeen]);

  const allInteractionsSeen =
    interactionsSeen &&
    allInteractions &&
    interactionsSeen.length === allInteractions.length;

  let scoreRaw = Object.values(scoreData as { [key: string]: number }).reduce<
    number
  >((acc, curr) => {
    // initialize _curr_ default
    let _curr_ = curr;

    // in the case where we might have exisitng minimized vales of 't' || 'f'
    //
    switch (_curr_.toString()) {
      case 't': {
        _curr_ = 1;
        break;
      }
      case 'f': {
        _curr_ = 0;
        break;
      }
      // and tap it again for good measure
      default:
        _curr_ = curr;
    }
    //

    // in the case where there could still exist a boolean
    // calculates fine implicitly but lets explicitly numberify it
    return acc + Number(_curr_);
  }, 0);

  let scorePercent = ((scoreRaw || 0) / numberOfScorableInteractions) * 100;

  // we'll default the score to 100 if. . . ?
  if (!moduleCompleteScoreRequirement) {
    // scorePercent = 100;
  }

  if (numberOfScorableInteractions <= 0) {
    scorePercent = 100;
  }

  const percentComplete =
    (interactionsSeen.length / allInteractions.length) * 100;

  const exitProps: ExitProps = {
    lastInteractionSeen,
    allInteractionsSeen,
    scoreRaw,
    scoreMax: numberOfScorableInteractions,
    passed: scorePercent >= Number(passMark),
    scorePercent,
    percentComplete,
    isComplete: false,
    moduleWasManuallySetComplete,
    sendScore: false,
    hasExited: exiting,
    shouldSendScoreWhen,
    moduleCompleteInterationRequirement,
    moduleCompleteScoreRequirement,
    sendOnlySuccessStatus,
    lessonStatus,
    scoringCriteria,
  };

  //
  if (moduleWasManuallySetComplete) {
    exitProps.isComplete = true;
  }

  //
  const setShouldSendScoreValue = setShouldSendScore(setIsComplete(exitProps));

  if (moduleWasManuallySetComplete) {
    setShouldSendScoreValue.isComplete = true;
  }

  SCORMToHosting({
    ...setShouldSendScoreValue,
    feedback,
  });

  // Initialise SCORM

  useEffect(() => {
    if (!initialised && !(window as any).hosted) {
      try {
        //

        if ((props as any)?.isInitialized) {
          // set it local to the module
          scormInitialized = true;
          setInitialized(true);
        } else {
          SCORM.version = (window as any).scormVersion || Version.OnePointTwo;
          //
          if (SCORM.init()) {
            scormInitialized = true;
            setInitialized(true);
          }
        }

        if (getSCORMIsInReviewMode(SCORM)) {
          dispatch({
            type: 'SET_MODULE_TO_REVIEW_MODE',
            data: {},
          });

          return;
        }

        // if any of the interactionsSeen contain sIds that do not exist we nuke:
        // suspend_data
        // cmi.location
        const remoteData = SCORM.get(API[version].suspend_data);

        if (remoteData) {
          const remoteDataJSONParsed = JSON.parse(remoteData) || {};

          const { interactionsSeen = [] } = remoteDataJSONParsed;

          const sIds = (Object.values(getStore().getState().blocks) as Block[])
            .filter(entry => {
              return entry?.type === 'Interaction' && entry?.props?.sId;
            })
            .map(entry => {
              return entry?.props?.sId;
            });

          if (interactionsSeen.some(entry => !sIds.includes(entry))) {
            //
            console.warn(
              'saved data contains interaction references that no longer exist in module. clearing out suspend_data and lessonLocation to reset'
            );

            SCORM.set(API[version].suspend_data, '');
            SCORM.set(API[version].location, '');
          }
        }

        //
      } catch (err) {
        window.console.log('scorm aint happy outside of LMS');
      }

      setVersion((SCORM.version as Version) || Version.TwoThousandAndFour);
    }
  }, [initialised]);

  // LOAD SAVED DATA
  useEffect(() => {
    if (window.location.href === 'http://localhost:3011/module.html') {
      return;
    }

    if (!initialised) {
      return;
    }

    // force incomplete if not explicitly marked as complete
    const completionStatus = SCORM.get(API_KEYS[version].completion);
    if (completionStatus !== CompletionString.Complete) {
      setSCORMCompletion(SCORM, version)(false);
    }

    //Get the latest suspend data
    const remoteData = SCORM.get(API[version].suspend_data);

    //
    let suspendedData: SuspendData = {
      interactionsSeen: [],
      interactionsLocked: [],
      landingPagesSeenState: {},
      interactionsReporting: {},
      scoreData: {},
      assessments: {},
      moduleVariables: {},
      location: '',
    };

    const state = getStore().getState();
    const { idToSIdMap, sIdToIdMap } = state;

    const isInReviewMode = getSCORMIsInReviewMode(SCORM);

    if (remoteData) {
      try {
        const remoteDataJSONParsed = JSON.parse(remoteData);

        let transformedInteractionsSeen: string[] = [];
        let transformedInteractionsLocked: string[] = [];

        if (remoteDataJSONParsed?.interactionsSeen?.length) {
          transformedInteractionsSeen = transformListFromSIdToId(
            sIdToIdMap,
            remoteDataJSONParsed.interactionsSeen
          );

          remoteDataJSONParsed.interactionsSeen = transformedInteractionsSeen;
        }

        if (remoteDataJSONParsed?.interactionsLocked?.length) {
          transformedInteractionsLocked = transformListFromSIdToId(
            sIdToIdMap,
            remoteDataJSONParsed.interactionsLocked
          );

          remoteDataJSONParsed.interactionsLocked = transformedInteractionsLocked;
        }

        let scoreData = remoteDataJSONParsed?.scoreData;
        if (scoreData) {
          remoteDataJSONParsed.scoreData = transformScoreDataFromSIdToId(
            scoreData,
            state
          );
        }

        let assessments = remoteDataJSONParsed?.assessments || {};
        if (Object.keys(assessments).length) {
          remoteDataJSONParsed.assessments = transformAssessmentsFromSuspendData(
            assessments,
            state
          );
        }

        suspendedData = {
          ...suspendedData, // defaults
          ...remoteDataJSONParsed,
        };
      } catch (err) {
        // console.log(err);
        suspendedData = {} as SuspendData;
      }
    }

    const entry = SCORM.get(API[version].entry);

    if (entry !== 'ab-initio' && enableBookmarking && !isInReviewMode) {
      const location =
        SCORM.get(API[version].location) || suspendedData.location;

      //

      dispatch({
        type: 'LOAD_BOOKMARK_DATA',
        data: location,
      });
    }

    if (suspendedData && enableBookmarking) {
      dispatch({
        type: 'LOAD_SUSPEND_DATA',
        data: suspendedData,
      });

      dispatch({
        type: 'UPDATE_INTERACTIONS_SEEN',
        data: suspendedData?.interactionsSeen,
      });
    }

    const reported = Object.keys(
      suspendedData?.interactionsReporting || {}
    ).reduce((k, next: string) => {
      return {
        ...k,
        [next]: true,
      };
    }, {});

    setReportedInteractions(reported);

    // for the moment we're disabling the rehydration of scoreData
    // well. we're gonna not save any so there'd be nada to rehydrate from
    // return;
    //
    if (
      suspendedData?.scoreData &&
      !hasReloadedScoreData
      // &&
      // enableBookmarking
    ) {
      // UPDATE !!!
      // we're gonna reload scoreData in all cases now
      // && suspendedData?.assessments
      //
      //
      // we check and only reload the scoreData if we have any scoring assessments
      // why ?
      // each question in an assessment locks when it's resolved ( correct. or has met max attempts )
      // so we can't ' double up ' on the score
      // if we're scoring mutlichoice / sortation and they're not locking on return
      // we want them to just do their scroing thing like on first run
      // so any results pages etc are reading the current session score correctly etc
      //
      const { scoreData } = suspendedData;

      if (scoreData) {
        // instead of putting this in a loop
        // could update the action > reducer to accept a single object ?
        // or add a new action > reducer to handle that case ?
        //

        console.log(scoreData, 'is dispatched and reloaded ?');

        Object.keys(scoreData).forEach((entry: any, index) => {
          //
          dispatch(updateScoreAction(entry, scoreData[entry]));
        });
      }

      hasReloadedScoreData = true;
    }

    if (isInReviewMode) {
      console.log('setModuleToReviewMode :', { isInReviewMode });
      dispatch({
        type: 'SET_MODULE_TO_REVIEW_MODE',
        data: {},
      });
    }

    //
  }, [initialised]);

  useEffect(() => {
    // clear saved data

    pubSub.subscribe('RESET_MODULE', () => {
      SCORM.set(API[version].suspend_data, '');
    });
  }, []);

  /**
   * This piece of code needs to change
   *
   * The goal is to execute this only once; and only when requested.
   *
   * Possible solution is to listen for events. This makes sense BUT
   * it seems to not work natively in redux; and that makes sense as
   * it creates quite a hard dependency
   *
   * Another possible solution is to use the store
   */

  useEffect(() => {
    let unloaded = false;

    const un = () => {
      if (unloaded || !initialised) {
        return;
      }

      if (exitComplete) {
        return;
      }

      const props = setCompleteOnExitOps(SCORM, version, exitProps);
      SCORMToHosting(props);

      unloaded = true;

      if (!scormControlledExit) {
        SCORM.handleExitMode = false;

        const completion = SCORM.get(API_KEYS[version].completion);

        let checkedScormExitMessage = '';
        //
        if (version === '1.2' && lessonStatus === 'passed/failed/incomplete') {
          // if passed || failed. send the scormExitMessage
          // else suspend

          checkedScormExitMessage = ['passed', 'failed'].includes(completion)
            ? scormExitMessage
            : 'suspend';
        } else {
          // do the thing we were already doing
          checkedScormExitMessage =
            completion !== CompletionString.Complete
              ? 'suspend'
              : scormExitMessage;
        }

        SCORM.set(API[version].exit, checkedScormExitMessage); // scormExitMessage
        SCORM.save();
        SCORM.quit();
      }
    };

    window.addEventListener('beforeunload', un);
    window.addEventListener('unload', un);

    return () => {
      window.removeEventListener('beforeunload', un);
      window.removeEventListener('unload', un);
    };
  }, [
    initialised,
    version,
    SCORM,
    exitProps,
    exitComplete,
    scormControlledExit,
    lastInteractionSeen,
  ]);

  useEffect(() => {
    if (!initialised) {
      return;
    }

    if (!exiting) {
      return;
    }

    if (exitComplete) {
      return;
    }

    const exitPropsProcessed = setCompleteOnExitOps(SCORM, version, exitProps);

    SCORMToHosting({
      ...exitPropsProcessed,
      feedback,
    });

    SCORM.handleExitMode = false;

    SCORM.set(API[version].exit, scormExitMessage);
    SCORM.save();

    let timeoutQuit;

    if (scormExitDelay) {
      SCORM.quit();
    } else {
      timeoutQuit = setTimeout(() => {
        SCORM.quit();
      }, 200);
    }

    const timeout = setTimeout(() => {
      if (scormControlledExit) {
        dispatch(setExitCompleteAction());
      }
    }, 1000);

    return () => {
      clearTimeout(timeout);
      clearTimeout(timeoutQuit);
    };
  }, [exiting, exitComplete, version, SCORM, exitProps]);

  /**
   * END SOLUTION REQUIREMENT
   */

  // SAVE LOCAL DATA ON CHANGES
  useEffect(() => {
    // TODO: remove the magic number here
    const delaySetSCORM = setTimeout(() => {
      //
      // my thinking here is we remove the question and response entries at the 'last minute'
      const editedInteractionsReporting = Object.keys(
        interactionsReporting
      ).reduce((acc, curr) => {
        const { question, response, ...$ } = interactionsReporting[curr];

        return {
          ...acc,
          [curr]: $,
        };
      }, {});

      const state = getStore().getState();
      const { idToSIdMap, sIdToIdMap, moduleVariables, sessionId } = state;

      const assessmentsWithDataRemoved = structuredClone(assessments);

      for (const entry in assessmentsWithDataRemoved) {
        if (assessmentsWithDataRemoved[entry].data) {
          assessmentsWithDataRemoved[entry].data = {};
        }
      }

      const assessmentsToSuspendData =
        assessmentsWithDataRemoved &&
        transformAssessmentsToSuspendData(assessmentsWithDataRemoved, state);

      const suspendData = {
        //
        // if we're not caring about rehydrating score...
        scoreData: transformScoreDataFromIdToSId(scoreData, state),
        //
        interactionsSeen: transformListFromIdToSId(
          idToSIdMap,
          interactionsSeen
        ),
        interactionsLocked: transformListFromIdToSId(
          idToSIdMap,
          interactionsLocked
        ),

        landingPagesSeenState,
        //
        // saving on LMS 'bandwidth'
        // disabling the sending of these for the moment
        // interactionsReporting data will appear in the cmi.interactions
        // interactionsReporting: editedInteractionsReporting,
        //
        // this will need to come back i think when we add in
        // the more targetted 'what assessment are we scoring'
        assessments: assessmentsToSuspendData,

        moduleVariables,
        ...(sessionId && {
          sessionId,
        }),
        //
        // !!!
        // location is not saved on state. but is directly stored in suspend_data
        // we need to ' cycle ' it back through so as not to be overridden to non-existent / '' / undefined
        location,
      };

      SCORMToHosting(suspendData, 'data');

      if (initialised) {
        SCORM.set(API[version].suspend_data, JSON.stringify(suspendData));
      }

      const props = setCompleteOnExitOps(SCORM, version, exitProps);

      SCORMToHosting(props);
    }, 100);

    return () => {
      clearTimeout(delaySetSCORM);
    };
  }, [
    initialised,
    interactionsSeen,
    landingPagesSeenState,
    interactionsLocked,
    interactionsReporting,
    lastInteractionSeen,
    assessments,
    moduleVariables,
  ]);

  const [ri, setRI] = useState<string[]>([]);

  // Interaction Reporting
  // Store information about a session and user doing sortations and
  // multichoices
  useEffect(() => {
    const completed = Object.keys(reportedInteractions);
    const reported = Object.keys(interactionsReporting);

    const delivered = reported
      .filter(x => !ri.includes(x))
      .filter(x => !completed.includes(x))
      .map(r => {
        const rep = interactionsReporting[r];

        if (initialised) {
          setCMIInteractions(
            rep.reportable as ReportableInteractions,
            version
          )(rep);
        }

        const resultParsed = getResult(version, rep.result);

        const qp = {
          id: rep.id,
          question: rep.question,
          type: rep.type,
          reportable: rep.reportable,
          response: rep.response ? rep.response : 'pending',
          correct: rep.correct ? rep.correct : 'pending',
          result: resultParsed as any, // this is BAD
          success: API_KEYS[version].result[rep.result],
        };

        SCORMToHosting({ [rep.id]: qp }, 'interactions');

        return r;
      });

    setRI([...ri, ...delivered]);
  }, [initialised, interactionsReporting]);

  // SAVE FEEDBACK AS INTERACTION REPORTING
  useEffect(() => {
    if (!initialised) {
      return;
    }

    const qid: qFunc = ({ id, key }) => {
      SCORM.set(`cmi.interactions.${key}.id`, id);
    };

    const qtype: qFunc = ({ key, type }) => {
      SCORM.set(`cmi.interactions.${key}.type`, type);
    };

    const qstudent: qFunc = ({ key, response }) => {
      SCORM.set(`cmi.interactions.${key}.student_response`, response);
    };

    for (const [key, value] of Object.entries(feedback)) {
      let count = SCORM.get('cmi.interactions._count');

      qid({ id: `rating-${key}-fill-in`, key: count } as any);
      qtype({ key: count, type: 'fill-in' } as any);
      qstudent({ key: count, response: `${value.rating}` } as any);

      SCORM.save();

      count = SCORM.get('cmi.interactions._count');
      qid({ id: `message-${key}`, key: count } as any);
      qtype({ key: count, type: 'fill-in' } as any);
      qstudent({ key: count, response: value.message } as any);

      SCORM.save();
    }
  }, [initialised, feedback]);

  // BOOKMARKING
  // when the section comes into view, store that in the scorm so that
  // we can get back there later
  useEffect(() => {
    // if there's only 1 interaction in the entire module. NO BOOKMARK
    if (allInteractions.length === 1) {
      console.log(
        'there is only 1 interaction in the module. nothing to bookmark'
      );
      return;
    }

    const setLocationOnSuspendData = location => {
      let suspend_data = SCORM.get(API[version].suspend_data);

      if (suspend_data) {
        suspend_data = JSON.parse(suspend_data);

        SCORM.set(
          API[version].suspend_data,
          JSON.stringify({
            ...suspend_data,
            location,
          })
        );
      }
    };

    const unsubscribe: any = pubSub.subscribe(
      'SECTION_IN_VIEW',
      ({ sectionInView }) => {
        const currentLocation = getCurrentLocation() as string;

        if (initialised && allInteractions[0]?.id === sectionInView?.id) {
          //
          // is first ' page ' of module. some logic to clear bookmarking ? set location to empty string'

          location = '';
          SCORM.set(API[version].location, location);
          setLocationOnSuspendData(location);

          return;
        }

        const [topicSId, interactionSId] =
          (currentLocation && currentLocation.split('/')) || [];
        const topic = getCurrentTopic();
        const interaction = getInteractionsInTopic(topic).find((b: any) => {
          const block = getBlockById(b.id);
          //
          return block && block?.props?.sId == interactionSId;
        }) as Block;

        // ignore any bookmarking that might happen within the quizEssment ( questions )

        const subType = interaction?.meta?.subType;

        if (topic?.props?.hiddenFromNextAndBackNavigation) {
          //   DO NOT BOOKMARK WITHIN TOPICS THAT ARE hiddenFromNextAndBackNavigation !!!

          return;
        }

        if (
          subType &&
          ['AssessmentQuestion', 'AssessmentResults'].includes(subType)
        ) {
          return;
        }
        //

        location = currentLocation;

        SCORMToHosting({
          location,
        });

        // adding a temp kinda bookmark on the localStorageState
        // uncomment etc as needed
        // setLocalStorageStateBookmark(location);

        if (initialised) {
          SCORM.set(API[version].location, location);
          //

          setLocationOnSuspendData(location);
        }
      }
    );

    return () => {
      unsubscribe();
    };
  }, [initialised]);

  return null;
};
